'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from './style.module.scss';

type DrawerPosition = 'left' | 'right';
type ButtonPosition = 'auto' | 'left' | 'right';

type Props = {
  toggleAriaLabel?: string;
  closeAriaLabel?: string;
  initialOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
  position?: DrawerPosition;
  buttonClassName?: string;
  overlayClassName?: string;
  closeBtnClassName?: string | null;
  drawerClassName?: string;
  drawerBodyClassName?: string;
  drawerContentClassName?: string;
  buttonPosition?: ButtonPosition;
  closeAnimationDuration?: number;
  backgroundImage?: string;
};

export default function DynamicDrawer({
  toggleAriaLabel = 'Toggle menu',
  closeAriaLabel = 'Close menu',
  initialOpen = false,
  onOpenChange,
  children,
  position = 'left',
  buttonClassName = '',
  overlayClassName = '',
  closeBtnClassName = '',
  drawerClassName = '',
  drawerBodyClassName = '',
  drawerContentClassName = '',
  buttonPosition = 'auto',
  closeAnimationDuration = 300,
  backgroundImage,
}: Props) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isMountedPortal, setIsMountedPortal] = useState(initialOpen);
  const [isClosing, setIsClosing] = useState(false);
  const portalRootRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const open = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
      setIsClosing(false);
    }
    setIsMountedPortal(true);
    requestAnimationFrame(() => {
      setIsOpen(true);
      onOpenChange?.(true);
      document.body.style.overflow = 'hidden';
    });
  };

  const close = () => {
    setIsOpen(false);
    setIsClosing(true);
    onOpenChange?.(false);
    document.body.style.overflow = '';

    closeTimerRef.current = window.setTimeout(() => {
      setIsClosing(false);
      setIsMountedPortal(false);
      closeTimerRef.current = null;
    }, closeAnimationDuration);
  };

  const toggle = () => (isOpen ? close() : open());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const portalRoot = document.createElement('div');
    portalRoot.setAttribute('data-dynamic-drawer-portal', 'true');
    document.body.appendChild(portalRoot);
    portalRootRef.current = portalRoot;
    return () => {
      if (portalRootRef.current?.parentNode) {
        portalRootRef.current.parentNode.removeChild(portalRootRef.current);
      }
      portalRootRef.current = null;
    };
  }, []);

  const onBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) close();
  };
  const onContentClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement | null;
    if (!el) return;

    const clickable = el.closest(
      'a[href], button, [data-close="true"]'
    ) as HTMLElement | null;
    if (!clickable) return;

    const noClose = (clickable.closest('[data-no-close="true"]') ||
      el.closest('[data-no-close="true"]')) as HTMLElement | null;
    if (noClose) return;

    if (clickable.tagName === 'A') {
      const href = (clickable as HTMLAnchorElement).getAttribute('href') || '';
      const target =
        (clickable as HTMLAnchorElement).getAttribute('target') || '';
    }

    close();
  };

  const resolvedButtonPos: DrawerPosition =
    buttonPosition === 'auto' ? position : (buttonPosition as DrawerPosition);

  const buttonPositionClass =
    resolvedButtonPos === 'left' ? cn.hamburgerLeft : cn.hamburgerRight;

  const shouldRenderPortal = Boolean(portalRootRef.current && isMountedPortal);

  const portalMarkup = (
    <>
      <div
        className={`${cn.drawerOverlay} ${
          isOpen ? cn.overlayOpen : cn.overlayClosed
        } ${overlayClassName}`}
        onMouseDown={onBackdropClick}
        role="presentation"
      />
      <div
        className={`${cn.drawerwrapper} ${
          position === 'left' ? cn.drawerLeft : cn.drawerRight
        } ${isOpen ? cn.drawerOpen : cn.drawerClosed} ${drawerClassName}`}
        role="dialog"
        aria-modal="true"
        style={{
          backgroundImage: backgroundImage
            ? `url(${backgroundImage})`
            : undefined,
        }}
      >
        {closeBtnClassName !== null && (
          <div className={`${cn.closeButton} ${closeBtnClassName}`}>
            <button
              aria-label={closeAriaLabel}
              className={cn.buttonElement}
              onClick={close}
            >
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
                  <path
                    d="M52.828,50,81.414,21.414a2,2,0,1,0-2.828-2.828L50,47.172,21.414,18.586a2,2,0,0,0-2.828,2.828L47.172,50,18.586,78.586a2,2,0,1,0,2.828,2.828L50,52.828,78.586,81.414a2,2,0,0,0,2.828-2.828Z"
                    transform="translate(-18 -18)"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}
        <div className={`${cn.drawerBody} ${drawerBodyClassName}`}>
          <div
            className={`${cn.drawerContent} ${drawerContentClassName}`}
            onClick={onContentClick}
            data-lenis-prevent
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <button
        type="button"
        aria-label={toggleAriaLabel}
        aria-expanded={isOpen}
        onClick={toggle}
        className={`${cn.hamburgerIcon} ${
          isOpen ? cn.hamburgerOpen : ''
        } ${buttonPositionClass} ${buttonClassName}`}
      >
        <span />
      </button>
      {portalRootRef.current
        ? createPortal(portalMarkup, portalRootRef.current)
        : null}
    </>
  );
}
